<template>
  <div class="bg-surface min-h-screen text-on-surface flex flex-col">
    <main class="w-full max-w-[1440px] mx-auto px-8 pt-40 pb-24 portal-home-font-2x flex-1">
      <div class="max-w-[1200px] mx-auto relative z-10">
        <div v-if="loading" class="py-32 text-center text-on-surface-variant text-xl">加载中...</div>
        <div v-else-if="!detail" class="py-32 text-center text-on-surface-variant text-xl">
          内容不存在或已下线
        </div>
        <template v-else>
          <div class="mb-12 inline-flex items-center gap-2 bg-surface-container/50 px-5 py-2 rounded-full text-sm font-body tracking-wide relative z-20">
            <a class="text-on-surface-variant hover:text-primary transition-colors" href="#" @click.prevent="goHomeSection('home')">首页</a>
            <span class="material-symbols-outlined text-[14px] text-outline-variant/60">chevron_right</span>
            <a class="text-on-surface-variant hover:text-primary transition-colors" href="#" @click.prevent="goNewsList">新闻公告中心</a>
            <span class="material-symbols-outlined text-[14px] text-outline-variant/60">chevron_right</span>
            <span class="text-primary font-medium">{{ breadcrumbDetailLabel }}</span>
          </div>

          <article class="max-w-none font-body text-on-surface leading-[1.8] space-y-12">
            <div class="mb-12 space-y-6">
              <div v-if="detail.isTop === '1'" class="flex items-center gap-4 text-base">
                <span class="rounded-full bg-secondary-container px-4 py-1.5 font-bold text-on-secondary-container">置顶</span>
              </div>
              <h1 class="text-5xl font-headline font-bold text-primary leading-tight">
                {{ detail.title }}
              </h1>
              <div class="flex flex-wrap items-center gap-6 text-on-surface-variant font-body text-base">
                <span class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-[22px]">person</span>
                  {{ detail.author || '四川省企联会管理创新工作部' }}
                </span>
                <span class="w-1.5 h-1.5 bg-outline-variant rounded-full"></span>
                <span class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-[22px]">calendar_today</span>
                  {{ formatPortalDate(detail.publishTime) }}
                </span>
              </div>
              <div class="h-1.5 w-24 bg-secondary rounded-full mt-8"></div>
            </div>

            <img
              v-if="detail.coverImage"
              :src="resolvePortalFileUrl(detail.coverImage)"
              class="w-full h-auto rounded-md object-cover border border-outline-variant/30 shadow-sm"
              alt="封面图"
            >

            <div class="portal-content-body" v-html="detail.contentBody"></div>

            <section
              v-if="attachments.length"
              class="mt-20 overflow-hidden rounded-lg border border-outline-variant/20 bg-surface-container-low p-10 shadow-sm relative"
            >
              <div class="absolute left-0 top-0 h-full w-[6px] bg-secondary"></div>
              <h3 class="mb-8 flex items-center gap-3 text-2xl font-headline font-bold text-primary">
                <span class="material-symbols-outlined text-secondary text-3xl">attach_file</span>
                附件下载
              </h3>
              <div class="space-y-6">
                <div
                  v-for="item in attachments"
                  :key="item.url"
                  class="flex flex-col gap-6 rounded-md border border-outline-variant/30 bg-surface-container-lowest p-6 transition-colors hover:border-secondary/50 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div class="flex items-center gap-5 min-w-0">
                    <div :class="attachmentIconClass(item.name)" class="flex h-12 w-12 shrink-0 items-center justify-center rounded">
                      <span class="material-symbols-outlined text-[28px]">{{ attachmentIcon(item.name) }}</span>
                    </div>
                    <div class="min-w-0">
                      <div class="truncate font-medium text-lg text-on-surface">{{ item.name }}</div>
                      <div class="text-sm text-on-surface-variant">{{ attachmentMeta(item.name) }}</div>
                    </div>
                  </div>
                  <a
                    :href="item.url"
                    class="inline-flex w-full items-center justify-center gap-2 rounded bg-secondary px-8 py-3 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-md sm:w-auto"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span class="material-symbols-outlined text-[22px]">download</span>
                    点击下载
                  </a>
                </div>
              </div>
            </section>
          </article>
        </template>
      </div>
    </main>

    <portal-footer />
  </div>
</template>

<script>
import {
  fetchPortalContentDetail,
  formatPortalDate,
  resolvePortalFileUrl,
  splitAttachmentUrls
} from '@/utils/content'
import PortalFooter from '@/components/PortalFooter.vue'

export default {
  name: 'ContentDetail',
  components: {
    PortalFooter
  },
  data () {
    return {
      loading: false,
      detail: null
    }
  },
  computed: {
    breadcrumbDetailLabel () {
      if (!this.detail) {
        return '新闻详情'
      }
      return this.detail.category === 'notice' ? '公告详情' : '新闻详情'
    },
    attachments () {
      return this.detail ? splitAttachmentUrls(this.detail.attachmentUrls) : []
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler () {
        this.loadDetail()
      }
    }
  },
  methods: {
    formatPortalDate,
    resolvePortalFileUrl,
    attachmentIcon (name = '') {
      const extension = name.split('.').pop().toLowerCase()
      if (extension === 'pdf') {
        return 'picture_as_pdf'
      }
      if (['doc', 'docx'].includes(extension)) {
        return 'description'
      }
      if (['xls', 'xlsx', 'csv'].includes(extension)) {
        return 'table_chart'
      }
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
        return 'image'
      }
      return 'attach_file'
    },
    attachmentIconClass (name = '') {
      const extension = name.split('.').pop().toLowerCase()
      if (extension === 'pdf') {
        return 'bg-red-100 text-red-700'
      }
      if (['doc', 'docx'].includes(extension)) {
        return 'bg-blue-100 text-blue-700'
      }
      if (['xls', 'xlsx', 'csv'].includes(extension)) {
        return 'bg-emerald-100 text-emerald-700'
      }
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
        return 'bg-violet-100 text-violet-700'
      }
      return 'bg-surface-container text-primary'
    },
    attachmentMeta (name = '') {
      const extension = name.includes('.') ? name.split('.').pop().toUpperCase() : 'FILE'
      return `${extension} 文档`
    },
    async loadDetail () {
      const contentId = this.$route.params.id
      if (!contentId) {
        this.detail = null
        return
      }
      this.loading = true
      try {
        this.detail = await fetchPortalContentDetail(contentId)
      } catch (error) {
        console.error('加载内容详情失败', error)
        this.detail = null
      } finally {
        this.loading = false
      }
    },
    goHomeSection (section) {
      this.$router.push({ path: '/home', query: { section } })
    },
    goNewsList () {
      this.$router.push({ path: '/content/list', query: { category: this.detail && this.detail.category === 'notice' ? 'notice' : 'news' } })
    },
    goComingSoon () {
      this.$router.push('/coming-soon')
    }
  }
}
</script>

<style scoped>
.portal-content-body {
  font-size: 2.25rem;
  line-height: 1.95;
}

.portal-content-body >>> img {
  display: block;
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
  margin: 3rem auto 1rem;
  border: 1px solid rgba(196, 198, 207, 0.3);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.portal-content-body >>> p {
  margin-bottom: 2rem;
}

.portal-content-body >>> h2 {
  margin-top: 4rem;
  margin-bottom: 2rem;
  border-left: 6px solid #c5a059;
  padding-left: 1.5rem;
  font-family: 'Work Sans', 'Microsoft YaHei', sans-serif;
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.4;
  color: #1b365d;
}

.portal-content-body >>> h3 {
  margin-top: 3rem;
  margin-bottom: 1.5rem;
  font-family: 'Work Sans', 'Microsoft YaHei', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #1b365d;
}

.portal-content-body >>> figcaption {
  margin-top: 1rem;
  text-align: center;
  font-size: 1.75rem;
  color: #74777f;
  font-style: italic;
}

.portal-content-body >>> figure {
  margin: 3rem 0;
}

.portal-content-body >>> a {
  color: #1b365d;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.portal-content-body >>> ul,
.portal-content-body >>> ol {
  margin: 1.5rem 0;
  padding-left: 1.5rem;
}

.portal-content-body >>> li {
  margin-bottom: 0.75rem;
}
</style>



// WEBPACK FOOTER //
// src/views/ContentDetail.vue